import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import BACKEND_URL from 'constants/backendUrl';
import { TaskMediaType } from 'core/enums/taskMediaType';
import { TaskPriority } from 'core/enums/taskPriority';
import { TaskSubject } from 'core/enums/taskSubject';
import Task from 'core/models/task';
import { isAfter } from 'date-fns';
import { DELETE, GET, POST, PUT } from 'utils/http';

// Couche d'accès « état serveur » des tâches via TanStack Query.
// Le cache (staleTime) remplace l'ancien flag Redux `alreadyFetched`, et
// l'invalidation de cette clé remplace les actions Set/Add/Update/Delete + refetch.
export const tasksQueryKey = ['tasks'] as const;

interface TasksResponse {
    tasks: Task[];
}

interface TaskMutationResponse {
    task: Task;
}

// Payload envoyé au backend lors de la création / mise à jour d'une tâche.
// Identique aux `Inputs` du TaskForm.
export interface TaskInputs {
    applicationId?: string;
    subject?: TaskSubject;
    customSubject?: string;
    contactId?: string;
    mediaType?: TaskMediaType;
    deadline: Date;
    priority: TaskPriority;
    comments?: string;
}

export const fetchTasks = async (): Promise<Task[]> => {
    const response = (await GET(`${BACKEND_URL}/api/tasks`)) as TasksResponse;

    return response.tasks ?? [];
};

export const useTasksQuery = () =>
    useQuery({
        queryKey: tasksQueryKey,
        queryFn: fetchTasks,
    });

// Comparateur de l'ancien sélecteur : tri croissant par deadline.
const byDeadlineAsc = (a: Task, b: Task): number => {
    if (isAfter(new Date(a.deadline), new Date(b.deadline))) {
        return 1;
    }

    return -1;
};

// tasksSelector(state) -> useTasks()
export const useTasks = () => useTasksQuery();

// taskByIdSelector(state, id) -> useTaskById(id)
export const useTaskById = (id: string | undefined) =>
    useQuery({
        queryKey: tasksQueryKey,
        queryFn: fetchTasks,
        select: (tasks: Task[]) => tasks.find((task) => task.id === id),
        enabled: Boolean(id),
    });

// tasksSortedByDeadlineSelector(state) -> useTasksSortedByDeadline()
// NB : on copie la liste avant de trier pour ne pas muter le cache (l'ancien sélecteur
// triait `state.tasks.list` en place).
export const useTasksSortedByDeadline = () =>
    useQuery({
        queryKey: tasksQueryKey,
        queryFn: fetchTasks,
        select: (tasks: Task[]) => [...tasks].sort(byDeadlineAsc),
    });

// tasksByContactIdSortedByDeadlineSelector(state, id) -> useTasksByContactIdSortedByDeadline(id)
export const useTasksByContactIdSortedByDeadline = (id: string | undefined) =>
    useQuery({
        queryKey: tasksQueryKey,
        queryFn: fetchTasks,
        select: (tasks: Task[]) =>
            tasks.filter((task) => task?.contact && task.contact.id === id).sort(byDeadlineAsc),
        enabled: Boolean(id),
    });

// tasksByApplicationIdSortedByDeadlineSelector(state, id) -> useTasksByApplicationIdSortedByDeadline(id)
export const useTasksByApplicationIdSortedByDeadline = (id: string | undefined) =>
    useQuery({
        queryKey: tasksQueryKey,
        queryFn: fetchTasks,
        select: (tasks: Task[]) =>
            tasks.filter((task) => task?.application && task.application.id === id).sort(byDeadlineAsc),
        enabled: Boolean(id),
    });

// tasksCount(state) -> useTasksCount()
export const useTasksCount = () =>
    useQuery({
        queryKey: tasksQueryKey,
        queryFn: fetchTasks,
        select: (tasks: Task[]) => tasks.length,
    });

// À appeler après une mutation pour resynchroniser la liste depuis le serveur.
export const useInvalidateTasks = () => {
    const queryClient = useQueryClient();

    return () => queryClient.invalidateQueries({ queryKey: tasksQueryKey });
};

// AddTask -> POST /api/task
export const useAddTaskMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (inputs: TaskInputs): Promise<Task> => {
            const response = (await POST<TaskMutationResponse>(
                `${BACKEND_URL}/api/task`,
                { ...inputs },
            )) as TaskMutationResponse;

            return response.task;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: tasksQueryKey }),
    });
};

// UpdateTask -> PUT /api/task/:id
export const useUpdateTaskMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, inputs }: { id: string; inputs: TaskInputs }): Promise<Task> => {
            const response = (await PUT<TaskMutationResponse>(`${BACKEND_URL}/api/task/${id}`, {
                ...inputs,
            })) as TaskMutationResponse;

            return response.task;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: tasksQueryKey }),
    });
};

// DeleteTask -> DELETE /api/task/:id
export const useDeleteTaskMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (taskId: string) => DELETE(`${BACKEND_URL}/api/task/${taskId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: tasksQueryKey }),
    });
};
